import React, { useState, useEffect } from "react";
import FormConfig from "../../Config/FormConfig.js";
import GenericForm from "../GenericForm/GenericForm";
import APIService from "../../Service/APIService";
import AccountManufacturer from "./AccountManufacturer";
import ProductPricing from "./ProductPricing";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Switch,
  Button,
  ConfigProvider,
  Form,
  Input,
  Col,
  Row,
  message,
  Steps,
  Upload,
  Modal,
  Alert,
} from "antd";
import ChildAccounts from "./ChildAccounts";
import { Content } from "antd/es/layout/layout";
import { Formik } from "formik";
import "./AccountDetails.css";
import { BooleanToggle } from "../../common/toggle/index.js";
import "./AccountDetailsRedesign.css";
import AccountUsers from "./AccountUsers";
import Swal from "sweetalert2";
import * as yup from "yup";
import {
  UploadOutlined,
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import Select from "react-select";
import { Flex, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useAuth } from "../AuthProvider.js";
import { hasPermission } from "../../Config/permissions.js";
import {
  DERIVED_FIELD_HELP,
  applyDerivedToValues,
  buildStateLookup,
  getDerivedFieldFlags,
  unmappedFieldHelp,
} from "../../utils/stateDerivedFields.js";
import Text from "../../common/typography/text.js";
import { colors } from "../../theme/colors.js";
import moment from "moment";
import RequestChangesModal from "./RequestChangesModal.js";
import {
  canRequestAccountChanges,
  getAccountChangeRequestBanner,
  isOwnAccountForChangeRequest,
} from "./accountChangeRequestAccess.js";

const BILLING_ACCOUNT_REQUIRED_MESSAGE = "Billing account is required";
const DISTRIBUTOR_REQUIRED_MESSAGE = "Distributor is required";

const STEP_INCOMPLETE_MESSAGE =
  "Complete the required fields before moving ahead.";

const ACCOUNT_EDIT_TABS = [
  { key: "shipping", label: "Shipping Details" },
  { key: "billing", label: "Billing Details" },
  { key: "account", label: "Account Details" },
  { key: "setup", label: "Account Setup" },
];

// Keeps Region / MAC / DME MAC equal to the StatesMaster mapping for the
// current shipping State (on load and on change). Renders nothing; a no-op
// when the state is unmapped, so unmapped states stay fully editable.
const StateDerivationSync = ({ formik, lookup }) => {
  const { values, setValues } = formik;
  React.useEffect(() => {
    const next = applyDerivedToValues(lookup, values);
    if (next !== values) setValues(next, false);
  }, [lookup, values, setValues]);
  return null;
};

// Step-by-step account creation. `requiredFields` mirrors the validation the
// create form already enforces, so a step only unlocks the next one once the
// fields the submit would fail on are filled in.
const CREATE_ACCOUNT_STEPS = [
  {
    key: "basics",
    title: "Basics",
    requiredFields: ["AccountName", "DistributorID"],
  },
  {
    // Region is derived from the shipping State (StatesMaster), so it is
    // required here rather than on Basics, once the state is known.
    key: "shipping",
    title: "Shipping Details",
    requiredFields: ["AddressLine1", "City", "State", "Zip", "RegionID"],
  },
  {
    key: "billing",
    title: "Billing Details",
    requiredFields: ["BillingAccountID"],
  },
  { key: "account", title: "Account Details", requiredFields: [] },
  { key: "setup", title: "Account Setup", requiredFields: [] },
  { key: "users", title: "Account Users", requiredFields: [] },
];

// Last step collects users, which needs an AccountID, so the account is created
// when the step before it is submitted.
const CREATE_USERS_STEP_INDEX = CREATE_ACCOUNT_STEPS.length - 1;
const CREATE_SUBMIT_STEP_INDEX = CREATE_USERS_STEP_INDEX - 1;

const hasFieldValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const requiredShippingText = (message) =>
  yup
    .string()
    .nullable()
    .required(message)
    .test("len", "Must be less than 255 characters", (val) => {
      if (val === null || val === undefined || val === "") return true;
      return val.length < 255;
    });

const isCreateStepComplete = (step, formik) =>
  (step?.requiredFields || []).every(
    (field) => hasFieldValue(formik.values?.[field]) && !formik.errors?.[field]
  );


const boolValue = (value) => value === true || value === 1 || value === "1";

const displayValue = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized || "N/A";
};

const displayCurrency = (value) => {
  const num = Number(value);
  if (value === null || value === undefined || value === "" || Number.isNaN(num)) {
    return "N/A";
  }
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

const formatCreatedOn = (value) => {
  if (!value) return "N/A";
  const date = moment(value);
  return date.isValid() ? date.format("MMMM Do, YYYY h:mm A") : "N/A";
};

// Missing values render "N/A" in a muted tone so populated fields stand out.
const AccountDetailItem = ({ label, children }) => (
  <div className="account-detail-item">
    <span className="account-detail-label">{label}</span>
    <div
      className={`account-detail-value${children === "N/A" ? " is-empty" : ""}`}
    >
      {children}
    </div>
  </div>
);

// One labelled row of the summary card: section title on the left rail,
// fields on a shared column grid so values line up across sections.
const AccountSummarySection = ({ title, children }) => (
  <div className="account-summary-section">
    <h3 className="account-summary-section-title">{title}</h3>
    <div className="account-summary-fields">{children}</div>
  </div>
);

const AccountStatusPill = ({
  label,
  active,
  trueLabel = "Enabled",
  falseLabel = "Disabled",
  falseVariant = "failure",
  action = null,
}) => (
  <span className={`account-status-pill ${active ? "success" : falseVariant}`}>
    <span className="account-status-dot" />
    <span className="account-status-key">{label}</span>
    {active ? trueLabel : falseLabel}
    {action}
  </span>
);

const AccountDetailsTab = ({ readOnly = false, accountDetails = null }) => {
  const params = useParams();
  const { user, isReady } = useAuth();
  const userData = user;
  const [fieldsArr, setFieldsArr] = React.useState([]);
  const [initialValues, setInitialValues] = React.useState(null);
  const [validationSchema, setValidationSchema] = React.useState(null);
  // Seed from the account already fetched by the parent so the summary renders
  // immediately instead of flashing a fullscreen spinner while we re-fetch.
  const [accountData, setAccountData] = React.useState(accountDetails);
  const [editMode, setEditMode] = React.useState(false);
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const [activeEditTab, setActiveEditTab] = React.useState("shipping");
  const [currentCreateStep, setCurrentCreateStep] = React.useState(0);
  const [createdAccount, setCreatedAccount] = React.useState(null);
  const [linkChange, setLinkChange] = React.useState(false);
  const [loading, setLoading] = React.useState(!accountDetails);
  const [requestChangesOpen, setRequestChangesOpen] = React.useState(false);
  const [latestUpdateRequest, setLatestUpdateRequest] = React.useState(null);
  const [updateRequestLoading, setUpdateRequestLoading] = React.useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const defaultName = queryParams.get("accountName");
  const defaultParent = queryParams.get("paID");
  const defaultParentName = queryParams.get("paName");
  const navigate = useNavigate();
  // const [selectedSegment, setSelectedSegment] = useState(defaultSegment);

  const getAccountData = async () => {
    const apiServe = new APIService();
    const response = await apiServe.getDataFromBackend(
      `getaccountbyid/?ID=${params.id}`
    );
    // console.log("Account Data: ", response);
    setAccountData(response?.data?.data);
    return response?.data?.data;
  };

  const isOwnAccount = isOwnAccountForChangeRequest(userData, params?.id);

  const loadLatestUpdateRequest = React.useCallback(async () => {
    if (!isReady || !isOwnAccount || !params?.id) {
      setLatestUpdateRequest(null);
      return;
    }
    setUpdateRequestLoading(true);
    try {
      const api = new APIService();
      const response = await api.getDataFromBackend(
        "getaccountupdaterequests?status=All&page=1&pageSize=1"
      );
      setLatestUpdateRequest(response?.data?.data?.[0] || null);
    } catch (error) {
      console.error("Failed to load account update request status", error);
      setLatestUpdateRequest(null);
    } finally {
      setUpdateRequestLoading(false);
    }
  }, [isOwnAccount, isReady, params?.id]);

  React.useEffect(() => {
    loadLatestUpdateRequest();
  }, [loadLatestUpdateRequest]);

  const [relatedAccountOptions, setRelatedAccountOptions] = React.useState([]);

  const getRelatedAccountOptions = async (ID, ParentAccountID) => {
    const serv = new APIService();
    console.log("ID: ", ID);
    console.log("Parent Account ID: ", ParentAccountID);
    const options = await serv.getDataFromBackend(
      `getallrelatedaccounts?ID=${ID}`
    );
    console.log("Related Accounts: ", options);
    let tempOptions = [];
    tempOptions.push({
      value: "Self",
      label: "Self",
      ParentAccountID: null,
    });
    options?.data?.data.map((ele) => {
      tempOptions.push({
        value: ele.ID,
        label: ele.AccountName,
        ParentAccountID: ele.ParentAccountID,
      });
    });
    tempOptions.push({
      value: defaultParent,
      label: defaultParentName,
      ParentAccountID: null,
    });
    // setRelatedAccountOptions(tempOptions);
    return tempOptions;
  };

  const generateValidationSchema = (formFieldArr) => {
    let obj = {}; // schema object
    for (const cur of formFieldArr) {
      obj[cur.backendName] = cur.validationFunction;
    }

    if (params?.id == null || params?.id == undefined) {
      obj.BillingAccountID = yup
        .mixed()
        .nullable()
        .test(
          "billing-account-required",
          BILLING_ACCOUNT_REQUIRED_MESSAGE,
          (value) => value !== null && value !== undefined && value !== ""
        );
      obj.DistributorID = yup
        .mixed()
        .nullable()
        .test(
          "distributor-required",
          DISTRIBUTOR_REQUIRED_MESSAGE,
          (value) => value !== null && value !== undefined && value !== ""
        );
      // Shipping address is captured in full when creating an account. Keeps
      // the 255-character bound the shared config already applies.
      obj.City = requiredShippingText("City is required");
      obj.State = requiredShippingText("State is required");
      obj.Zip = requiredShippingText("ZIP is required");
    }

    return obj;
  };

  const [stateOptions, setStateOptions] = React.useState([]);
  const [stateRows, setStateRows] = React.useState([]);

  const getStates = async () => {
    const apiServe = new APIService();
    const response = await apiServe.getDataFromBackend("getstatesmaster");
    // console.log("States: ", response);
    const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
    setStateRows(rows);
    let options = [];
    rows.map((ele) => {
      options.push({ value: ele.ID, label: ele.Abbreviation });
    });
    return options;
  };

  React.useEffect(() => {
    // console.log("useEffect called account details tab");
    console.log("Default Name: ", defaultName);
    let val = null;
    if (params?.id == null || params?.id == undefined) {
      setIsEditable(true);
      getStates().then((options) => {
        setStateOptions(options);
      });
    }
    else {
      // console.log("Account ID: ", params.id);
      getStates().then((options) => {
        setStateOptions(options);
      });
      getAccountData()
        .then(async (data) => {
          const distOptions = await getDistributorOptions();
          val = generateFieldsArray(distOptions, data);
          setValidationSchema(
            yup.object().shape(generateValidationSchema(val))
          );
          getAllAccountOptions(params?.id);
          getAllRegions().then((options) => {
            setRegionOptions(options);
          });
        })
        .then(() => {
          setLoading(false);
        });
      return;
    }
    getAllAccountOptions();
    val = generateFieldsArray();
    setValidationSchema(yup.object().shape(generateValidationSchema(val)));
    getDistributorOptions();
    if (defaultParent) {
      getRelatedAccountOptions(defaultParent).then((data) => {
        console.log("Related Account Options: ", data);
        setRelatedAccountOptions(data);
      });
    }
    console.log("Account component mounted");
    getAllRegions()
      .then((options) => {
        setRegionOptions(options);
      })
      .then(() => {
        setLoading(false);
      });
  }, [linkChange]);

  const [accountOptions, setAccountOptions] = React.useState(null);
  const [allAccountsData, setAllAccountsData] = React.useState([]);
  const getAllAccountOptions = async (ID, ParentAccountID) => {
    const serv = new APIService();
    // console.log("ID: ", ID);
    // console.log("Parent Account ID: ", ParentAccountID);
    const options = await serv.getDataFromBackend(`getaccounts`);
    console.log("All Accounts: ", options?.data?.data);
    setAllAccountsData(options?.data?.data);
    let tempOptions = [];
    tempOptions.push({
      value: "Self",
      label: "Self",
      ParentAccountID: null,
    });
    options?.data?.data.map((ele) => {
      tempOptions.push({
        value: ele.ID,
        label: ele.AccountName,
        ParentAccountID: ele.ParentAccountID,
      });
    });

    setAccountOptions(tempOptions);
  };

  const generateInitialValues = (distOptions, fieldVal, data = null) => {
    let obj = {};
    if (!data) {
      for (const cur of fieldVal) {
        obj[cur.backendName] = null;
        if (cur.backendName === "EnableIVR") {
          obj[cur.backendName] = true;
        }
      }
    } else {
      for (const cur of fieldVal) {
        obj[cur.backendName] =
          cur.backendName in data ? data[cur.backendName] : null;
      }
    }
    if (params?.id == null || params?.id == undefined) {
      if (defaultName) {
        obj.AccountName = defaultName;
      }
    }
    console.log("Initial Values:", obj);
    if (defaultParent) {
      obj.ParentAccountID = Number(defaultParent);
      obj.IsChildAccount = true;
    }
    obj.DistributorName = obj?.DistributorName
      ? obj?.DistributorName
      : distOptions?.find((ele) => ele.value === obj?.DistributorID)?.label;
    if (obj["AccountBAAAzureBlobName"]) {
      setUploadFileList([
        { name: obj["AccountBAAFileName"], status: "done", uid: "1" },
      ]);
      setFileName(obj["AccountBAAFileName"]);
      setAccountBAAFileBlobName(obj["AccountBAAAzureBlobName"]);
    }
    return obj;
  };

  const generateFieldsArray = (distOptions, data = null) => {
    const accountFields = FormConfig.CreateAccountForm;
    // console.log(accountFields);
    setFieldsArr(accountFields);
    setInitialValues(generateInitialValues(distOptions, accountFields, data));
    return accountFields;
  };

  const submitHandler = async (values, formikHelpers) => {
    if (readOnly) return;
    const isCreateSubmit = params?.id == null || params?.id === undefined;
    // In create mode the form is a wizard: only the Account Setup step submits,
    // and never twice (the account already exists once we reach Account Users).
    if (isCreateSubmit && (createdAccount || currentCreateStep !== CREATE_SUBMIT_STEP_INDEX)) {
      return;
    }
    const { setFieldError, setFieldTouched } = formikHelpers;
    console.log("Submit Handler: ", values);
    const normalizedPastDueWarning =
      values?.PastDueWarning === null ||
      values?.PastDueWarning === undefined ||
      values?.PastDueWarning === ""
        ? null
        : Number(values.PastDueWarning);
    const normalizedPastDueLimit =
      values?.PastDueLimit === null ||
      values?.PastDueLimit === undefined ||
      values?.PastDueLimit === ""
        ? null
        : Number(values.PastDueLimit);

    if (
      normalizedPastDueWarning !== null &&
      normalizedPastDueLimit !== null &&
      normalizedPastDueLimit < normalizedPastDueWarning
    ) {
      setFieldTouched("PastDueLimit", true, false);
      setFieldError(
        "PastDueLimit",
        "PastDueLimit must be greater than or equal to PastDueWarning"
      );
      message.error(
        "Past Due Limit must be greater than or equal to Past Due Warning."
      );
      return;
    }

    values.IsFileUpdated = isFileUpdated;
    // check if baa is true
    // if (values?.BAA) {
    //   if (!fileName) {
    //     message.error("If BAA is true, please upload the BAA file");
    //     return;
    //   }
    // }
    if (accountBAAFile) {
      // set BAA as true
      values.BAA = true;
    } else {
      // set BAA as false
      values.BAA = false;
    }
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to save these changes?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const formData = new FormData();
          const apiServe = new APIService();
          let response = null;
          if (params?.id == null || params?.id == undefined) {
            const stringifiedPostObj = JSON.stringify(values);
            formData.append("file", accountBAAFile);
            formData.append("data", stringifiedPostObj);
            response = await apiServe.addFileDataToBackend(
              formData,
              "addaccount",
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
          } else {
            // console.log("Update Account", values);
            let postObj = values;
            if (isAccountNameChanged) {
              postObj = {
                ...values,
                IsAccountNameChanged: true,
              };
            }
            const stringifiedPostObj = JSON.stringify(postObj);
            formData.append("file", accountBAAFile);
            formData.append("data", stringifiedPostObj);
            response = await apiServe.addFileDataToBackend(
              formData,
              "updateaccount",
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
                params: { ID: params.id },
              }
            );
          }

          // if (params?.id == null || params?.id == undefined) {
          //   response = await apiServe.addDataToBackend(values, 'addaccount');
          //   // console.log("Submit AHndler Response: ", response);
          // }
          // else {
          //   // console.log("Update Account", values);
          //   let postObj = values;
          //   if(isAccountNameChanged){
          //     postObj = {
          //       ...values,
          //       IsAccountNameChanged: true
          //     }
          //   }
          //   response = await apiServe.updateDataToBackend(postObj, 'updateaccount');
          //   // console.log("Submit Handler Response: ", response);
          // }
          if (response?.status === 200 || response?.status === 201) {
            Swal.fire({
              icon: "success",
              title: "Changes Saved",
              text: "Your changes have been saved successfully",
            }).then(() => {
              if (params?.id != null) {
                // Existing account edit: close the modal and refresh in place.
                setEditModalOpen(false);
                setIsEditable(false);
                window.location.reload();
                return;
              }

              const newAccID = response?.data?.accountID;
              if (newAccID) {
                // Keep the wizard mounted and move on to Account Users, which
                // needs the ID the backend just handed back.
                setCreatedAccount({ ...values, ID: newAccID });
                setCurrentCreateStep(CREATE_USERS_STEP_INDEX);
              } else if (values?.ParentAccountID && defaultParent) {
                navigate(`/Account/${values?.ParentAccountID}`);
                window.location.reload();
              }
            });
            return;
          }

          Swal.fire({
            icon: "error",
            title: "Error",
            text: "An error has occurred",
          });
        } catch (error) {
          const errorMessage =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "An error has occurred";

          if (
            errorMessage === BILLING_ACCOUNT_REQUIRED_MESSAGE ||
            errorMessage === "Selected billing account does not exist" ||
            errorMessage === "Invalid BillingAccountID"
          ) {
            setFieldTouched("BillingAccountID", true, false);
            setFieldError("BillingAccountID", errorMessage);
          }

          if (errorMessage.startsWith("PastDueWarning")) {
            setFieldTouched("PastDueWarning", true, false);
            setFieldError("PastDueWarning", errorMessage);
          }

          if (
            errorMessage.startsWith("PastDueLimit") ||
            errorMessage ===
              "PastDueLimit must be greater than or equal to PastDueWarning"
          ) {
            setFieldTouched("PastDueLimit", true, false);
            setFieldError("PastDueLimit", errorMessage);
          }

          Swal.fire({
            icon: "error",
            title: "Error",
            text: errorMessage,
          });
        }
      }
    });
  };

  const enableEditMode = (checked) => {
    console.log("Checked: ", checked);
    setEditMode(checked);
  };
  const [isEditable, setIsEditable] = useState(false);
  const submitHandlerUtil = (values) => {
    console.log("Submit Handler Util: ", values);
    submitHandler(values);
  };
  const [distributorOptions, setDistributorOptions] = React.useState([]);
  const getDistributorOptions = async () => {
    const apiServe = new APIService();
    const response = await apiServe.getDataFromBackend("getdistributors");
    // console.log("Distributors: ", response);
    let tempOptions = [];
    response?.data?.data.map((ele) => {
      tempOptions.push({
        value: ele.ID,
        label: ele.DistributorName,
      });
    });
    setDistributorOptions(tempOptions);
    return tempOptions;
  };

  const handleBillingAccountChange = (value) => {
    console.log("Billing Account Value", value);
    const accObj = allAccountsData.find(
      (ele) => Number(ele.ID) === Number(value)
    );
    console.log("Account Object: ", accObj);
    return accObj;
  };

  const [regions, setRegions] = React.useState([]);
  const [regionOptions, setRegionOptions] = React.useState([]);

  const getAllRegions = async () => {
    const apiServe = new APIService();
    const response = await apiServe.getDataFromBackend("getallregions");
    // console.log("Regions: ", response);
    setRegions(response?.data?.data);
    let options = [];
    response?.data?.data.map((ele) => {
      options.push({ value: ele.ID, label: ele.Region });
    });
    return options;
  };

  const handleLinkChange = async () => {
    await setLinkChange(!linkChange);
    window.location.reload();
  };

  const showCannotDeleteAccountModal = () =>
    Swal.fire({
      title: "Account cannot be deleted",
      text: "This account cannot be deleted because of associated ivrs, invoices, orders. Please delete the associated records first and then proceed with account deletion.",
      icon: "error",
      confirmButtonText: "Okay",
      confirmButtonColor: "#1677ff",
    });

  const deleteAccount = async () => {
    if (readOnly) return;
    if (!params?.id) {
      return;
    }

    const apiServe = new APIService();

    try {
      setLoading(true);
      const eligibilityResponse = await apiServe.updateDataToBackend(
        {
          AccountID: Number(params.id),
          ValidateOnly: true,
        },
        "deleteaccount"
      );
      setLoading(false);

      if (eligibilityResponse?.data?.canDelete !== true) {
        await showCannotDeleteAccountModal();
        return;
      }

      const confirmation = await Swal.fire({
        title: "Delete Account",
        text: "Are you sure you want to delete this account? This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
        confirmButtonColor: "#ff4d4f",
        cancelButtonText: "Cancel",
      });

      if (!confirmation.isConfirmed) {
        return;
      }

      setLoading(true);
      const deleteResponse = await apiServe.updateDataToBackend(
        {
          AccountID: Number(params.id),
        },
        "deleteaccount"
      );
      setLoading(false);

      if (deleteResponse?.status === 200) {
        await Swal.fire({
          icon: "success",
          title: "Account Deleted",
          text: "Account has been deleted successfully.",
        });
        navigate("/AccountDashboard");
        return;
      }

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error has occurred",
      });
    } catch (error) {
      setLoading(false);
      if (
        error?.response?.status === 409 ||
        error?.response?.data?.canDelete === false
      ) {
        await showCannotDeleteAccountModal();
        return;
      }

      const errorMessage =
        error?.response?.data?.message || "An error has occurred";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
      });
    }
  };

  const handleButtonClick = (submitForm) => {
    if (readOnly) return;
    if (isEditable) {
      submitForm(); // Trigger form submission
    } else {
      setIsEditable(true); // Enable edit mode
    }
  };

  const openEditModal = () => {
    if (readOnly) return;
    setIsEditable(true);
    setActiveEditTab("shipping");
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setIsEditable(false);
  };

  const [isAccountNameChanged, setIsAccountNameChanged] = useState(false);
  const [accountBAAFile, setAccountBAAFile] = useState(null);
  const [accountBAAFileBlobName, setAccountBAAFileBlobName] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [uploadFileList, setUploadFileList] = useState([]);
  const [isFileUpdated, setIsFileUpdated] = useState(false);

  const handleFile = async ({ file, onSuccess }) => {
    if (readOnly) return;
    if (file.size > 4194304) {
      message.error("File size should be less than 4MB");
      setFileName(null);
      setAccountBAAFile(null);
      setUploadFileList([]);
      return;
    }
    setFileName(file.name);
    setIsFileUpdated(true);
    setUploadFileList([{ name: file.name, status: "done", uid: "1" }]);
    setAccountBAAFile(file);
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const downloadFile = async () => {
    // show loader
    setLoading(true);
    const apiServe = new APIService();
    const sasUrl = await apiServe.getSasToken(accountBAAFileBlobName);

    const exists = await apiServe.checkBlobExists(sasUrl);
    if (!exists) {
      message.error("File does not exist.");
      return;
    }
    // download the file from Azure Blob Storage
    const link = document.createElement("a");
    link.href = sasUrl;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // hide loader
    setLoading(false);
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    // action: 'https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload',
    accept: [".pdf"],
    // disabled: !isEditable,
    fileList: uploadFileList,
    customRequest: handleFile,
    onPreview: downloadFile,
    onRemove: (file) => {
      setAccountBAAFile(null);
      setUploadFileList([]);
      setFileName(null);
      setIsFileUpdated(true);
    },
    onChange(info) {
      console.log("File Info: ", info);
      const { status } = info.file;
      // console.log("Status: ", status);
      if (status !== "uploading") {
        // console.log(info.file, info.fileList);
      }
      if (status === "done") {
        // setIvrReport(info.file.originFileObj);
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };

  const isCreate = params?.id == null || params?.id === undefined;
  const isAdmin = userData?.Type === "Admin";
  const canRequestChanges = canRequestAccountChanges({
    user: userData,
    routeAccountId: params?.id,
    readOnly,
  });
  const updateRequestBanner = isOwnAccount
    ? getAccountChangeRequestBanner(latestUpdateRequest)
    : null;
  const parentAccountName =
    accountData?.ParentAccountID && Array.isArray(allAccountsData)
      ? allAccountsData.find(
          (ele) => Number(ele.ID) === Number(accountData.ParentAccountID)
        )?.AccountName
      : null;

  // Two-line US format: "5511 Marg, Ste 100" / "Pittsburg, PA 15206".
  const summaryAddress = (line1, line2, city, state, zip) => {
    const clean = (...parts) =>
      parts.map((part) => String(part ?? "").trim()).filter(Boolean);
    const cityState = clean(city, state).join(", ");
    return [clean(line1, line2).join(", "), clean(cityState, zip).join(" ")].filter(
      Boolean
    );
  };

  const summaryAddressLines = (keyPrefix, ...parts) => {
    const lines = summaryAddress(...parts);
    return lines.length
      ? lines.map((line) => <span key={`${keyPrefix}-${line}`}>{line}</span>)
      : "N/A";
  };

  const summaryCreatedOn = formatCreatedOn(accountData?.createdAt);
  const summaryCreatedBy = String(accountData?.CreatedByUserName ?? "").trim();

  // Region / MAC / DME MAC follow the shipping State (StatesMaster). The
  // lookup mirrors the backend rule; StateDerivationSync keeps the form values
  // equal to what a save will persist, on load and whenever State changes.
  const stateLookup = React.useMemo(
    () => buildStateLookup(stateRows, regionOptions),
    [stateRows, regionOptions],
  );
  const derivedFor = (formik) => getDerivedFieldFlags(stateLookup, formik.values.State);
  const derivedHelp = (formik, field, label) =>
    derivedFor(formik)[field]
      ? DERIVED_FIELD_HELP
      : unmappedFieldHelp(label, formik.values.State);

  const renderTextField = (formik, name, label, opts = {}) => (
    <div className="account-edit-field">
      <label htmlFor={name}>
        {label}
        {opts.required ? <span>*</span> : null}
      </label>
      <Input
        id={name}
        name={name}
        type={opts.type || "text"}
        min={opts.type === "number" ? "0" : undefined}
        step={opts.type === "number" ? "0.01" : undefined}
        value={formik.values[name] ?? ""}
        disabled={opts.disabled}
        placeholder={opts.placeholder}
        onBlur={() => formik.setFieldTouched(name, true)}
        onChange={(event) => {
          const raw = event.target.value;
          const next = opts.numeric ? (raw === "" ? null : raw) : raw;
          formik.setFieldValue(name, next);
          if (opts.onChange) opts.onChange(next, event);
        }}
      />
      {opts.helpText ? (
        <div className="account-edit-help">{opts.helpText}</div>
      ) : null}
      {formik.errors[name] && formik.touched[name] ? (
        <div className="account-edit-error">{formik.errors[name]}</div>
      ) : null}
    </div>
  );

  const renderSelectField = (
    formik,
    { name, label, value, options, onChange, required = false, disabled = false, helpText }
  ) => (
    <div className="account-edit-field">
      <label>
        {label}
        {required ? <span>*</span> : null}
      </label>
      <Select
        aria-label={label}
        value={value}
        options={options}
        onChange={onChange}
        onBlur={() => formik.setFieldTouched(name)}
        isDisabled={disabled}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 2000 }) }}
      />
      {helpText ? <div className="account-edit-help">{helpText}</div> : null}
      {formik.errors[name] && formik.touched[name] ? (
        <div className="account-edit-error">{formik.errors[name]}</div>
      ) : null}
    </div>
  );

  const renderBillingAccountChange = (formik) => (option) => {
    // Commit the selection and its derived address fields in one Formik update.
    // Calling setFieldValue for each field starts several validations against
    // different snapshots; a later address-field validation can therefore put
    // back the required error for the previous (null) BillingAccountID.
    let billingValues;
    if (!option) {
      billingValues = {
        BillingAccountName: null,
        BillingAccountID: null,
        BillingAddressLine1: null,
        BillingAddressLine2: null,
        BillingCity: null,
        BillingState: null,
        BillingZip: null,
      };
    } else if (option.value === "Self") {
      billingValues = {
        BillingAccountName: option.label,
        BillingAccountID: option.value,
        BillingAddressLine1: formik.values?.AddressLine1,
        BillingAddressLine2: formik.values?.AddressLine2,
        BillingCity: formik.values?.City,
        BillingState: formik.values?.State,
        BillingZip: formik.values?.Zip,
      };
    } else {
      const account = handleBillingAccountChange(option.value);
      billingValues = {
        BillingAccountName: option.label,
        BillingAccountID: option.value,
        BillingAddressLine1: account?.AddressLine1,
        BillingAddressLine2: account?.AddressLine2,
        BillingCity: account?.City,
        BillingState: account?.State,
        BillingZip: account?.Zip,
      };
    }

    formik.setFieldTouched("BillingAccountID", true, false);
    formik.setValues({ ...formik.values, ...billingValues }, true);
  };

  const renderTopFields = (formik) => (
    <>
      <div className="account-edit-grid two">
        {!isCreate
          ? renderTextField(formik, "AccountNumber", "Account Number", {
              disabled: true,
            })
          : null}
        {renderTextField(formik, "AccountName", "Account Name", {
          required: true,
          onChange: (next) => {
            if (
              params?.id &&
              formik.values?.ID === formik.values?.BillingAccountID
            ) {
              formik.setFieldValue("BillingAccountName", next);
              setIsAccountNameChanged(true);
            }
          },
        })}
      </div>
      <div className="account-edit-grid two">
        {renderSelectField(formik, {
          name: "DistributorID",
          label: "Distributor",
          required: isCreate,
          value: {
            label: formik.values.DistributorName,
            value: formik.values.DistributorID,
          },
          options: distributorOptions,
          onChange: (option) => {
            formik.setFieldValue("DistributorName", option.label);
            formik.setFieldValue("DistributorID", option.value);
          },
        })}
        {renderSelectField(formik, {
          name: "RegionID",
          label: "Region",
          required: true,
          value: {
            label: formik.values.RegionName,
            value: formik.values.RegionID,
          },
          options: regionOptions,
          disabled: derivedFor(formik).RegionID,
          helpText: derivedHelp(formik, "RegionID", "region"),
          onChange: (option) => {
            formik.setFieldValue("RegionID", option.value);
            formik.setFieldValue("RegionName", option.label);
          },
        })}
      </div>
      <div className="account-edit-grid two">
        {renderTextField(formik, "Phone", "Phone")}
        {renderTextField(formik, "Fax", "Fax")}
      </div>
    </>
  );

  const renderShippingTab = (formik) => (
    <>
      <div className="account-edit-grid two">
        {renderTextField(formik, "AddressLine1", "Address Line 1", {
          required: true,
        })}
        {renderTextField(formik, "AddressLine2", "Address Line 2")}
      </div>
      <div className="account-edit-grid city-state-zip">
        {renderTextField(formik, "City", "City", { required: isCreate })}
        {renderSelectField(formik, {
          name: "State",
          label: "State",
          required: isCreate,
          value: { label: formik.values.State, value: formik.values.State },
          options: stateOptions,
          onChange: (option) => formik.setFieldValue("State", option.label),
        })}
        {renderTextField(formik, "Zip", "ZIP", { required: isCreate })}
      </div>
    </>
  );

  const renderBillingTab = (formik) => (
    <>
      <div className="account-edit-grid two">
        {renderSelectField(formik, {
          name: "BillingAccountID",
          label: "Billing Account",
          required: isCreate,
          value: {
            value: formik.values.BillingAccountID,
            label: formik.values.BillingAccountName,
          },
          options: defaultParent ? relatedAccountOptions : accountOptions,
          onChange: renderBillingAccountChange(formik),
        })}
        {renderTextField(formik, "APContact", "AP Contact")}
      </div>
      <div className="account-edit-grid two">
        {renderTextField(formik, "BillingAddressLine1", "Billing Address Line 1")}
        {renderTextField(formik, "BillingAddressLine2", "Billing Address Line 2")}
      </div>
      <div className="account-edit-grid city-state-zip">
        {renderTextField(formik, "BillingCity", "Billing City")}
        {renderTextField(formik, "BillingState", "Billing State")}
        {renderTextField(formik, "BillingZip", "Billing ZIP")}
      </div>
    </>
  );

  const renderAccountTab = (formik) => (
    <>
      <div className="account-edit-grid two">
        {renderTextField(formik, "MedicareAdminContractor", "Medicare Admin Contractor", {
          disabled: derivedFor(formik).MedicareAdminContractor,
          helpText: derivedHelp(formik, "MedicareAdminContractor", "Medicare Admin Contractor"),
        })}
        {renderTextField(formik, "DMEMAC", "DME MAC", {
          disabled: derivedFor(formik).DMEMAC,
          helpText: derivedHelp(formik, "DMEMAC", "DME MAC"),
        })}
      </div>
      <div className="account-edit-grid two">
        {renderTextField(formik, "NPI", "NPI")}
      </div>
      <div className="account-edit-grid two">
        {renderTextField(formik, "TIN", "TIN")}
        {renderTextField(formik, "PTAN", "PTAN")}
      </div>
      <div className="account-edit-grid two">
        {renderTextField(formik, "EIN", "EIN")}
        <div className="account-edit-field">
          <label>Account BAA File</label>
          <Upload {...uploadProps} disabled={readOnly}>
            <Button icon={<UploadOutlined />}>Upload</Button>
          </Upload>
        </div>
      </div>
    </>
  );

  const renderSetupTab = (formik) => (
    <>
      <div className="account-edit-grid two">
        {renderTextField(formik, "QuickbooksID", "Quickbooks ID")}
        <div className="account-edit-field">
          <label htmlFor="HubspotRecordID">Hubspot Record ID</label>
          <div className="account-edit-field-inline">
            <Input
              id="HubspotRecordID"
              name="HubspotRecordID"
              type="text"
              value={formik.values.HubspotRecordID ?? ""}
              disabled
              placeholder="Feature Coming Soon"
              onChange={(event) =>
                formik.setFieldValue("HubspotRecordID", event.target.value)
              }
            />
            <Button disabled>Verify</Button>
          </div>
        </div>
      </div>
      <div className="account-edit-grid two">
        {renderTextField(formik, "PastDueWarning", "Past Due Warning", {
          type: "number",
          numeric: true,
          helpText: "Balance past due before a warning is shown on the account",
        })}
        {renderTextField(formik, "PastDueLimit", "Past Due Limit", {
          type: "number",
          numeric: true,
          helpText: "Balance past due before the account is restricted from ordering",
        })}
      </div>
      <div className="pg-toggle-grid">
        <BooleanToggle
          label="Enable IVR"
          value={formik.values.EnableIVR}
          onChange={(value) => formik.setFieldValue("EnableIVR", value)}
        />
      </div>
    </>
  );

  const renderActiveTab = (formik) => {
    switch (activeEditTab) {
      case "billing":
        return renderBillingTab(formik);
      case "account":
        return renderAccountTab(formik);
      case "setup":
        return renderSetupTab(formik);
      case "shipping":
      default:
        return renderShippingTab(formik);
    }
  };

  const renderEditFormInner = (formik) => (
    <>
      {renderTopFields(formik)}
      <div className="account-edit-tabs">
        {ACCOUNT_EDIT_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`account-edit-tab ${
              activeEditTab === tab.key ? "active" : ""
            }`}
            onClick={() => setActiveEditTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {renderActiveTab(formik)}
    </>
  );

  // -------------------------
  // Create-account wizard
  // -------------------------
  const getMaxReachableCreateStep = (formik) => {
    if (createdAccount) return CREATE_USERS_STEP_INDEX;

    let reachable = 0;
    for (let index = 0; index < CREATE_SUBMIT_STEP_INDEX; index += 1) {
      if (!isCreateStepComplete(CREATE_ACCOUNT_STEPS[index], formik)) break;
      reachable = index + 1;
    }
    return reachable;
  };

  const touchCreateStepFields = (formik, stepIndex) => {
    (CREATE_ACCOUNT_STEPS[stepIndex]?.requiredFields || []).forEach((field) =>
      formik.setFieldTouched(field, true, true)
    );
  };

  const handleCreateStepChange = (formik) => (nextStep) => {
    if (nextStep === currentCreateStep) return;
    if (createdAccount) return;

    if (nextStep <= getMaxReachableCreateStep(formik)) {
      setCurrentCreateStep(nextStep);
      return;
    }

    touchCreateStepFields(formik, currentCreateStep);
    message.error(STEP_INCOMPLETE_MESSAGE);
  };

  const handleCreateStepNext = (formik) => () => {
    if (!isCreateStepComplete(CREATE_ACCOUNT_STEPS[currentCreateStep], formik)) {
      touchCreateStepFields(formik, currentCreateStep);
      message.error(STEP_INCOMPLETE_MESSAGE);
      return;
    }

    setCurrentCreateStep((prev) => Math.min(prev + 1, CREATE_SUBMIT_STEP_INDEX));
  };

  const handleCreateStepBack = () =>
    setCurrentCreateStep((prev) => Math.max(prev - 1, 0));

  const renderCreateStepContent = (formik) => {
    switch (CREATE_ACCOUNT_STEPS[currentCreateStep]?.key) {
      case "shipping":
        return renderShippingTab(formik);
      case "billing":
        return renderBillingTab(formik);
      case "account":
        return renderAccountTab(formik);
      case "setup":
        return renderSetupTab(formik);
      case "users":
        return (
          <>
            <div className="account-create-step-note">
              {`${createdAccount?.AccountName || "The account"} has been created. Add the users for this account, or finish to open it.`}
            </div>
            <AccountUsers accountData={createdAccount} refreshInPlace />
          </>
        );
      case "basics":
      default:
        return renderTopFields(formik);
    }
  };

  const renderCreateWizardFooter = (formik) => {
    if (currentCreateStep === CREATE_USERS_STEP_INDEX) {
      return (
        <div className="account-edit-modal-footer">
          <button
            type="button"
            className="account-save-button"
            onClick={() => navigate(`/Account/${createdAccount?.ID}`)}
          >
            Finish
          </button>
        </div>
      );
    }

    return (
      <div className="account-edit-modal-footer account-create-footer">
        <div>
          {currentCreateStep > 0 && (
            <button
              type="button"
              className="account-secondary-button"
              onClick={handleCreateStepBack}
            >
              Back
            </button>
          )}
        </div>
        {currentCreateStep === CREATE_SUBMIT_STEP_INDEX ? (
          <button type="submit" className="account-save-button">
            Create Account
          </button>
        ) : (
          <button
            type="button"
            className="account-save-button"
            onClick={handleCreateStepNext(formik)}
          >
            Next
          </button>
        )}
      </div>
    );
  };

  const renderCreateWizard = (formik) => {
    const maxReachableStep = getMaxReachableCreateStep(formik);

    return (
      <form
        onSubmit={formik.handleSubmit}
        className="account-edit-modal account-create-wizard"
      >
        <div className="account-edit-modal-header">
          <Text variant="h1">Create Account</Text>
        </div>
        <div className="account-edit-divider" />
        <ConfigProvider
          theme={{
            token: {
              fontFamily: "Figtree, sans-serif",
              colorText: colors["primaryColor"],
              colorPrimary: colors["primaryColor"],
            },
          }}
        >
          <Steps
            className="account-create-steps"
            size="small"
            labelPlacement="vertical"
            current={currentCreateStep}
            onChange={handleCreateStepChange(formik)}
            items={CREATE_ACCOUNT_STEPS.map((step, index) => ({
              title: step.title,
              disabled: createdAccount
                ? index !== CREATE_USERS_STEP_INDEX
                : index > maxReachableStep,
            }))}
          />
        </ConfigProvider>
        <div className="account-edit-modal-body">
          {renderCreateStepContent(formik)}
        </div>
        {renderCreateWizardFooter(formik)}
      </form>
    );
  };

  return (
    <div>
      <Content>
        {loading && !accountData && (
          <Flex align="center" gap="middle">
            <Spin
              indicator={
                <LoadingOutlined
                  style={{
                    fontSize: 48,
                  }}
                  spin
                />
              }
              fullscreen
            />
          </Flex>
        )}
        {!isCreate && accountData && (
          <>
          {updateRequestBanner ? (
            <Alert
              type={updateRequestBanner.type}
              showIcon
              className="account-update-request-banner"
              message={updateRequestBanner.message}
              description={updateRequestBanner.description}
            />
          ) : null}
          <section className="account-summary-card">
            <header className="account-summary-header">
              <div className="account-summary-heading">
                {summaryCreatedOn !== "N/A" || summaryCreatedBy ? (
                  <p className="account-summary-meta">
                    Created
                    {summaryCreatedOn !== "N/A" ? (
                      <> on <strong>{summaryCreatedOn}</strong></>
                    ) : null}
                    {summaryCreatedBy ? (
                      <> by <strong>{summaryCreatedBy}</strong></>
                    ) : null}
                  </p>
                ) : null}
                <div className="account-summary-flags">
                  <AccountStatusPill
                    label="IVR"
                    active={boolValue(accountData.EnableIVR)}
                  />
                  <AccountStatusPill
                    label="BAA"
                    active={boolValue(accountData.BAA)}
                    trueLabel="Added"
                    falseLabel="Not Added"
                    falseVariant="warning"
                    action={
                      boolValue(accountData.BAA) && accountBAAFileBlobName ? (
                        <button
                          type="button"
                          className="account-status-pill-action"
                          onClick={downloadFile}
                          title="Download BAA file"
                          aria-label="Download BAA file"
                        >
                          <DownloadOutlined />
                        </button>
                      ) : null
                    }
                  />
                </div>
              </div>

              {!readOnly &&
                ((isAdmin &&
                  (hasPermission(userData, "manage_accounts.edit") ||
                    (userData?.IsSuperAdmin === true &&
                      hasPermission(userData, "manage_accounts.delete")))) ||
                  canRequestChanges) && (
                <div className="account-summary-actions">
                  {isAdmin && hasPermission(userData, "manage_accounts.edit") && (
                    <button
                      type="button"
                      className="account-action-button primary"
                      onClick={openEditModal}
                    >
                      <EditOutlined />
                      Edit
                    </button>
                  )}
                  {isAdmin && userData?.IsSuperAdmin === true && hasPermission(userData, "manage_accounts.delete") && (
                    <button
                      type="button"
                      className="account-action-button danger"
                      onClick={deleteAccount}
                    >
                      <DeleteOutlined />
                      Delete
                    </button>
                  )}
                  {canRequestChanges && (
                    <button
                      type="button"
                      className="account-action-button primary"
                      disabled={
                        updateRequestLoading || latestUpdateRequest?.Status === "Pending"
                      }
                      onClick={() => setRequestChangesOpen(true)}
                    >
                      {latestUpdateRequest?.Status === "Pending"
                        ? "Request Pending"
                        : "Request Changes"}
                    </button>
                  )}
                </div>
              )}
            </header>

            <AccountSummarySection title="Overview">
              <AccountDetailItem label="Account Name">
                {displayValue(accountData.AccountName)}
              </AccountDetailItem>
              {boolValue(accountData.IsChildAccount) && parentAccountName ? (
                <AccountDetailItem label="Child Account of">
                  {displayValue(parentAccountName)}
                </AccountDetailItem>
              ) : null}
              <AccountDetailItem label="Distributor">
                {displayValue(accountData.DistributorName)}
              </AccountDetailItem>
              <AccountDetailItem label="Region">
                {displayValue(accountData.RegionName)}
              </AccountDetailItem>
            </AccountSummarySection>

            <AccountSummarySection title="Contact">
              <AccountDetailItem label="Phone">
                {displayValue(accountData.Phone)}
              </AccountDetailItem>
              <AccountDetailItem label="FAX">
                {displayValue(accountData.Fax)}
              </AccountDetailItem>
              <AccountDetailItem label="Shipping Address">
                {summaryAddressLines(
                  "ship",
                  accountData.AddressLine1,
                  accountData.AddressLine2,
                  accountData.City,
                  accountData.State,
                  accountData.Zip
                )}
              </AccountDetailItem>
              <AccountDetailItem label="Billing Address">
                {summaryAddressLines(
                  "bill",
                  accountData.BillingAddressLine1,
                  accountData.BillingAddressLine2,
                  accountData.BillingCity,
                  accountData.BillingState,
                  accountData.BillingZip
                )}
              </AccountDetailItem>
            </AccountSummarySection>

            <AccountSummarySection title="Medicare">
              <AccountDetailItem label="Medicare Admin Contractor">
                {displayValue(accountData.MedicareAdminContractor)}
              </AccountDetailItem>
              <AccountDetailItem label="DME MAC">
                {displayValue(accountData.DMEMAC)}
              </AccountDetailItem>
              <AccountDetailItem label="NPI">
                {displayValue(accountData.NPI)}
              </AccountDetailItem>
              <AccountDetailItem label="PTAN">
                {displayValue(accountData.PTAN)}
              </AccountDetailItem>
            </AccountSummarySection>

            <AccountSummarySection title="Billing">
              <AccountDetailItem label="TIN">
                {displayValue(accountData.TIN)}
              </AccountDetailItem>
              <AccountDetailItem label="EIN">
                {displayValue(accountData.EIN)}
              </AccountDetailItem>
              <AccountDetailItem label="Past Due Warning">
                {displayCurrency(accountData.PastDueWarning)}
              </AccountDetailItem>
              <AccountDetailItem label="Past Due Limit">
                {displayCurrency(accountData.PastDueLimit)}
              </AccountDetailItem>
            </AccountSummarySection>

            <AccountSummarySection title="Integrations">
              <AccountDetailItem label="Quickbooks ID">
                {displayValue(accountData.QuickbooksID)}
              </AccountDetailItem>
              <AccountDetailItem label="Hubspot Record ID">
                {displayValue(accountData.HubspotRecordID)}
              </AccountDetailItem>
            </AccountSummarySection>

            <div className="account-summary-section">
              <h3 className="account-summary-section-title">Account Note</h3>
              <p
                className={`account-summary-note-text${
                  displayValue(accountData.AccountNote) === "N/A" ? " is-empty" : ""
                }`}
              >
                {displayValue(accountData.AccountNote)}
              </p>
            </div>
          </section>
          </>
        )}

        {!isCreate && accountData && isOwnAccount ? (
          <RequestChangesModal
            open={requestChangesOpen}
            account={accountData}
            onCancel={() => setRequestChangesOpen(false)}
            onSubmitted={(request) => {
              setLatestUpdateRequest(request);
              setRequestChangesOpen(false);
            }}
          />
        ) : null}

        {initialValues && (
          <Formik
            onSubmit={submitHandler}
            initialValues={initialValues}
            validationSchema={validationSchema}
          >
            {(formik) => (
              <>
              <StateDerivationSync formik={formik} lookup={stateLookup} />
              {isCreate ? (
                renderCreateWizard(formik)
              ) : (
                <Modal
                  open={editModalOpen}
                  centered
                  footer={null}
                  closable={false}
                  width={600}
                  onCancel={closeEditModal}
                  className="account-edit-modal-shell"
                  destroyOnClose
                >
                  <form
                    onSubmit={formik.handleSubmit}
                    className="account-edit-modal"
                  >
                    <div className="account-edit-modal-header">
                      <Text variant="h1">Edit Account</Text>
                      <button
                        type="button"
                        className="account-icon-button"
                        onClick={closeEditModal}
                      >
                        <CloseOutlined />
                      </button>
                    </div>
                    <div className="account-edit-divider" />
                    <div className="account-edit-modal-body">
                      {renderEditFormInner(formik)}
                    </div>
                    <div className="account-edit-modal-footer">
                      <button
                        type="button"
                        className="account-secondary-button"
                        onClick={closeEditModal}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="account-save-button">
                        Save Changes
                      </button>
                    </div>
                  </form>
                </Modal>
              )}
              </>
            )}
          </Formik>
        )}
        {fieldsArr.length > 0 && !isCreate && (
          <div>
            {/* {accountData &&
            <Switch defaultChecked={false} onChange={enableEditMode} />
          } */}
            {/* <GenericForm fieldsArr={fieldsArr} data={accountData} submitHandler={submitHandlerUtil} editMode={editMode} screen="Account"/> */}
            {accountOptions &&
              accountData?.IsChildAccount !== true &&
              params?.id && (
                <ChildAccounts
                  accountData={accountData}
                  handleLinkChange={handleLinkChange}
                  accountOptions={accountOptions}
                  allAccountsData={allAccountsData}
                  readOnly={readOnly}
                />
              )}
            <AccountUsers accountData={accountData} readOnly={readOnly} />
            {accountData?.IsChildAccount !== true && params?.id && (
              <AccountManufacturer accountData={accountData} readOnly={readOnly} />
            )}
            {accountData?.IsChildAccount !== true && params?.id && (
              <ProductPricing accountData={accountData} readOnly={readOnly} />
            )}
          </div>
        )}
      </Content>
    </div>
  );
};

export default AccountDetailsTab;
